import { MediaObject, SelectedImage } from 'types'
import { useRootStore } from 'stores/RootStore'
import { ImagePickerDrawer } from 'components/ImagePickerModal/ImagePickerDrawer/ImagePickerDrawer'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { ImagePickerContextState } from 'components/ImagePickerModal/ImagePickerContextTypes'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCreateMediaObjects } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { AddFromAnotherSourceButton } from 'components/Wizards/SelectImages/AddFromAnotherSourceButton'
import { MobileAddFromAnotherSourceButtonProps } from 'components/Wizards/SelectImages/Types'

export const MobileAddFromAnotherSourceButton = ({ onAdd, ...props }: MobileAddFromAnotherSourceButtonProps) => {
  const { drawersStore } = useRootStore()
  const { openDrawer, closeDrawer } = drawersStore
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
  const { mutateAsync: createMediaObjects } = useCreateMediaObjects()

  const { uploadImageFiles } = useImportMediaFiles(false, () => {
    showToastMessage('All files were uploaded successfully!', 'success')
  })

  const uploadImages = async (files: File[], context: ImagePickerContextState) => {
    let objects: MediaObject[] = []
    if (context.provider === 'uploads') {
      const uploadedImages = await uploadImageFiles(files, uploadsProjectId)
      if (!uploadedImages) return []
      return uploadedImages.map(
        (file) => ({ id: file.id, url: file.source_url, mime_type: file.mime_type } as SelectedImage),
      )
    } else {
      objects = await createMediaObjects(files.map((file) => ({ file, filename: file.name })))
    }

    return objects.map(
      (file) =>
        ({
          id: file.id.toString(),
          url: file.source_url,
          mime_type: file.mime_type,
          original_filename: file.id.toString(),
        } as SelectedImage),
    )
  }

  const handlePress = () => {
    openDrawer(ImagePickerDrawer, {
      accept: 'image/png, image/jpeg, image/gif',
      title: 'Add From Another Source',
      uploadImages,
      onConfirm: async (pickerImages) => {
        onAdd(pickerImages)
        closeDrawer()
      },
      onCancel: () => {
        closeDrawer()
      },
    })
  }

  return <AddFromAnotherSourceButton onPress={handlePress} {...props} size="lg" variant="tertiary" fullWidth />
}
